import { Link } from "react-router-dom";
import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import ExportExcel from "../../components/ExportExcel";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import * as XLSX from "xlsx";

function BusDetails() {
  const [busDetails, setBusDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;

  useEffect(() => {
    const fetchBusDetails = async () => {
      try {
        const response = await axios.get(`${server}/allBusDetailsMongoDb`, {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        });
        if (response.data && response.data["body-json"]?.body?.data) {
          setBusDetails(response.data["body-json"].body.data);
        } else {
          console.error("Invalid data format from API", response.data);
        }
      } catch (error) {
        console.error("Error fetching bus details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusDetails();
  }, []);

  const handleLeftBus = async (bus) => {
    const vehicleNumber = bus.generalInfo?.vehicleNumber;
    const busNumber = bus.generalInfo?.busNumber;

    const result = await MySwal.fire({
      title: "Mark as Left?",
      text: `Are you sure you want to mark Bus ${busNumber} (${vehicleNumber}) as left?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Mark as Left!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          "https://q1xgsyocc2.execute-api.ap-south-1.amazonaws.com/Prod/LeftBus",
          { vehicleNumber, busNumber, action: "remove" },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );
        if (response.status === 200) {
          toast.success(`Bus ${busNumber} marked as left`, {
            position: "top-right",
          });
          setBusDetails((prev) =>
            prev.filter((b) => b.generalInfo?.vehicleNumber !== vehicleNumber),
          );
        } else {
          toast.error("Action failed. Please try again.", {
            position: "top-right",
          });
        }
      } catch (error) {
        toast.error("Error occurred. Please try again.", {
          position: "top-right",
        });
      }
    }
  };

  // Filter
  const filteredRecords = busDetails.filter((bus) => {
    const searchLower = searchTerm.toLowerCase();

    return (
      Object.values(bus.generalInfo || {}).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(searchLower),
      ) ||
      Object.values(bus.insurance || {}).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(searchLower),
      )
    );
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  // Sort
  filteredRecords.sort((a, b) => {
    const numA = a.generalInfo?.busNumber?.match(/\d+/)?.[0] ?? 0;
    const numB = b.generalInfo?.busNumber?.match(/\d+/)?.[0] ?? 0;
    return Number(numA) - Number(numB);
  });

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const columnMapping = {
    "generalInfo.busNumber": "Bus Number",
    "generalInfo.vehicleNumber": "Vehicle Number",
    "generalInfo.ownerName": "Owner Name",
    "generalInfo.chassisNumber": "Chassis Number",
    "generalInfo.engineNumber": "Engine Number",
    "generalInfo.numberOfSeats": "Total Seats",
    "generalInfo.gprsStatus": "GPRS Status",
    "generalInfo.cctvInstalled": "CCTV Installed",
    "insurance.insuranceCompany": "Insurance Company",
    "insurance.insurancePolicyNumber": "Insurance Policy Number",
    "insurance.insuranceAmount": "Insurance Amount",
  };

  const handleExportExcel = () => {
    ExportExcel(busDetails, "BusDetails", columnMapping);
  };

  const handleDelete = async (vehicleNumberToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete bus "${vehicleNumberToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_busDetails_usingMongodb`,
          { vehicleNumber: vehicleNumberToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Bus Details Deleted", { position: "top-right" });
          setBusDetails((prev) =>
            prev.filter(
              (bus) => bus.generalInfo?.vehicleNumber !== vehicleNumberToDelete,
            ),
          );
        } else {
          toast.error("Please Try Again");
        }
      } catch (error) {
        toast.error("Please Try Again");
      }
    }
  };

  const handleExportAllBusesExcel = () => {
    if (!busDetails || busDetails.length === 0) {
      toast.error("No bus details to export");
      return;
    }

    const sheetData = busDetails.map((bus) => {
      const gen = bus.generalInfo || {};
      const ins = bus.insurance || {};

      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
      };

      return {
        "Bus Number": gen.busNumber || "",
        "Vehicle Number": gen.vehicleNumber || "",
        "Owner Name": gen.ownerName || "",
        "Chassis Number": gen.chassisNumber || "",
        "Engine Number": gen.engineNumber || "",
        "Total Seats": gen.numberOfSeats || "",
        "GPRS Status": gen.gprsStatus || "",
        "CCTV Installed": gen.cctvInstalled || "",
        "Tax Paid Date From": formatDate(gen.tasPaidDate),
        "Tax Paid Date To": formatDate(gen.tasPaidExpiryDate),
        "Fitness Date From": formatDate(gen.fitenessDate),
        "Fitness Date To": formatDate(gen.fitnessRenewalDate),
        "Pollution Control Date From": formatDate(gen.pollutionControlDate),
        "Pollution Control Date To": formatDate(gen.pollutionControlExpiryDate),
        "Insurance Company": ins.insuranceCompany || "",
        "Insurance Policy Number": ins.insurancePolicyNumber || "",
        "Insurance Amount": ins.insuranceAmount || "",
        "Insurance Date From": formatDate(ins.insuranceDate),
        "Insurance Date To": formatDate(ins.insuranceExpiryDate),
      };
    });

    sheetData.sort((a, b) => {
      const numA = parseInt(a["Bus Number"]?.match(/\d+/)?.[0]) || 0;
      const numB = parseInt(b["Bus Number"]?.match(/\d+/)?.[0]) || 0;
      return numA - numB;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Buses");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllBuses_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportAllBusesCCTVStatusExcel = () => {
    if (!busDetails || busDetails.length === 0) {
      toast.error("No bus details to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const cctvYes = [];
    const cctvNo = [];

    busDetails.forEach((bus) => {
      const gen = bus.generalInfo || {};
      const ins = bus.insurance || {};

      const row = {
        "Bus Number": gen.busNumber || "",
        "Vehicle Number": gen.vehicleNumber || "",
        "Owner Name": gen.ownerName || "",
        "Chassis Number": gen.chassisNumber || "",
        "Engine Number": gen.engineNumber || "",
        "Total Seats": gen.numberOfSeats || "",
        "GPRS Status": gen.gprsStatus || "",
        "CCTV Installed": gen.cctvInstalled || "",
        "Tax Paid Date From": formatDate(gen.tasPaidDate),
        "Tax Paid Date To": formatDate(gen.tasPaidExpiryDate),
        "Fitness Date From": formatDate(gen.fitenessDate),
        "Fitness Date To": formatDate(gen.fitnessRenewalDate),
        "Pollution Control Date From": formatDate(gen.pollutionControlDate),
        "Pollution Control Date To": formatDate(gen.pollutionControlExpiryDate),
        "Insurance Company": ins.insuranceCompany || "",
        "Insurance Policy Number": ins.insurancePolicyNumber || "",
        "Insurance Amount": ins.insuranceAmount || "",
        "Insurance Date From": formatDate(ins.insuranceDate),
        "Insurance Date To": formatDate(ins.insuranceExpiryDate),
      };

      if (gen.cctvInstalled?.toLowerCase() === "yes") {
        cctvYes.push(row);
      } else {
        cctvNo.push(row);
      }
    });

    const sortByBusNumber = (arr) =>
      arr.sort((a, b) => {
        const numA = parseInt(a["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        const numB = parseInt(b["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        return numA - numB;
      });

    sortByBusNumber(cctvYes);
    sortByBusNumber(cctvNo);

    const workbook = XLSX.utils.book_new();

    if (cctvYes.length > 0) {
      const wsYes = XLSX.utils.json_to_sheet(cctvYes);
      XLSX.utils.book_append_sheet(workbook, wsYes, "CCTV Yes");
    }

    if (cctvNo.length > 0) {
      const wsNo = XLSX.utils.json_to_sheet(cctvNo);
      XLSX.utils.book_append_sheet(workbook, wsNo, "CCTV No");
    }

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllBuses_CCTVStatus_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportAllBusesGPRSStatusExcel = () => {
    if (!busDetails || busDetails.length === 0) {
      toast.error("No bus details to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const gprsInstalled = [];
    const gprsNotInstalled = [];

    busDetails.forEach((bus) => {
      const gen = bus.generalInfo || {};
      const ins = bus.insurance || {};

      const row = {
        "Bus Number": gen.busNumber || "",
        "Vehicle Number": gen.vehicleNumber || "",
        "Owner Name": gen.ownerName || "",
        "Chassis Number": gen.chassisNumber || "",
        "Engine Number": gen.engineNumber || "",
        "Total Seats": gen.numberOfSeats || "",
        "GPRS Status": gen.gprsStatus || "",
        "CCTV Installed": gen.cctvInstalled || "",
        "Tax Paid Date From": formatDate(gen.tasPaidDate),
        "Tax Paid Date To": formatDate(gen.tasPaidExpiryDate),
        "Fitness Date From": formatDate(gen.fitenessDate),
        "Fitness Date To": formatDate(gen.fitnessRenewalDate),
        "Pollution Control Date From": formatDate(gen.pollutionControlDate),
        "Pollution Control Date To": formatDate(gen.pollutionControlExpiryDate),
        "Insurance Company": ins.insuranceCompany || "",
        "Insurance Policy Number": ins.insurancePolicyNumber || "",
        "Insurance Amount": ins.insuranceAmount || "",
        "Insurance Date From": formatDate(ins.insuranceDate),
        "Insurance Date To": formatDate(ins.insuranceExpiryDate),
      };

      if (gen.gprsStatus?.toLowerCase() === "installed") {
        gprsInstalled.push(row);
      } else {
        gprsNotInstalled.push(row);
      }
    });

    const sortByBusNumber = (arr) =>
      arr.sort((a, b) => {
        const numA = parseInt(a["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        const numB = parseInt(b["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        return numA - numB;
      });

    sortByBusNumber(gprsInstalled);
    sortByBusNumber(gprsNotInstalled);

    const workbook = XLSX.utils.book_new();

    if (gprsInstalled.length > 0) {
      const wsInstalled = XLSX.utils.json_to_sheet(gprsInstalled);
      XLSX.utils.book_append_sheet(workbook, wsInstalled, "GPRS Installed");
    }

    if (gprsNotInstalled.length > 0) {
      const wsNotInstalled = XLSX.utils.json_to_sheet(gprsNotInstalled);
      XLSX.utils.book_append_sheet(
        workbook,
        wsNotInstalled,
        "GPRS Not Installed",
      );
    }

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllBuses_GPRSStatus_${today}.xlsx`);

    toast.success("Report Generated");
  };

  const handleExportAllBusesOwnerWiseExcel = () => {
    if (!busDetails || busDetails.length === 0) {
      toast.error("No bus details to export");
      return;
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? "" : d.toLocaleDateString("en-GB");
    };

    const ownerMap = {};

    busDetails.forEach((bus) => {
      const gen = bus.generalInfo || {};
      const ins = bus.insurance || {};
      const ownerName = gen.ownerName || "Unknown";

      const row = {
        "Bus Number": gen.busNumber || "",
        "Vehicle Number": gen.vehicleNumber || "",
        "Owner Name": ownerName,
        "Chassis Number": gen.chassisNumber || "",
        "Engine Number": gen.engineNumber || "",
        "Total Seats": gen.numberOfSeats || "",
        "GPRS Status": gen.gprsStatus || "",
        "CCTV Installed": gen.cctvInstalled || "",
        "Tax Paid Date From": formatDate(gen.tasPaidDate),
        "Tax Paid Date To": formatDate(gen.tasPaidExpiryDate),
        "Fitness Date From": formatDate(gen.fitenessDate),
        "Fitness Date To": formatDate(gen.fitnessRenewalDate),
        "Pollution Control Date From": formatDate(gen.pollutionControlDate),
        "Pollution Control Date To": formatDate(gen.pollutionControlExpiryDate),
        "Insurance Company": ins.insuranceCompany || "",
        "Insurance Policy Number": ins.insurancePolicyNumber || "",
        "Insurance Amount": ins.insuranceAmount || "",
        "Insurance Date From": formatDate(ins.insuranceDate),
        "Insurance Date To": formatDate(ins.insuranceExpiryDate),
      };

      if (!ownerMap[ownerName]) {
        ownerMap[ownerName] = [];
      }

      ownerMap[ownerName].push(row);
    });

    const sortByBusNumber = (arr) =>
      arr.sort((a, b) => {
        const numA = parseInt(a["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        const numB = parseInt(b["Bus Number"]?.match(/\d+/)?.[0]) || 0;
        return numA - numB;
      });

    const workbook = XLSX.utils.book_new();

    Object.entries(ownerMap).forEach(([ownerName, rows]) => {
      sortByBusNumber(rows);
      const ws = XLSX.utils.json_to_sheet(rows);

      const safeSheetName = ownerName
        .substring(0, 31)
        .replace(/[:\\/?*\[\]]/g, "_");
      XLSX.utils.book_append_sheet(workbook, ws, safeSheetName || "Unknown");
    });

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `AllBuses_OwnerWise_${today}.xlsx`);

    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((bus, index) => (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Vehicle No</span> &mdash;{" "}
                              {bus.generalInfo?.vehicleNumber} <br />
                              <span class="text-dark">Bus No</span> &mdash;{" "}
                              {bus.generalInfo?.busNumber} <br />
                              <span class="text-dark">
                                Total Seat
                              </span> &mdash; {bus.generalInfo?.numberOfSeats}
                              <br />
                              <span class="text-dark">
                                Chasis No
                              </span> &mdash; {bus.generalInfo?.chassisNumber}{" "}
                              <br />
                              <span class="text-dark">
                                Engine No
                              </span> &mdash; {bus.generalInfo?.engineNumber}{" "}
                              <br />
                              <span class="text-dark">
                                Tax Date
                              </span> &mdash;{" "}
                              {new Date(
                                bus.generalInfo?.tasPaidDate,
                              ).toLocaleDateString("en-GB")}{" "}
                              To{" "}
                              {new Date(
                                bus.generalInfo?.tasPaidExpiryDate,
                              ).toLocaleDateString("en-GB")}
                              <br />
                              <span class="text-dark">Fitness Date</span>{" "}
                              &mdash;{" "}
                              {new Date(
                                bus.generalInfo?.fitenessDate,
                              ).toLocaleDateString("en-GB")}{" "}
                              To{" "}
                              {new Date(
                                bus.generalInfo?.fitnessRenewalDate,
                              ).toLocaleDateString("en-GB")}
                              <br />
                              <span class="text-dark">Pollution Date</span>{" "}
                              &mdash;{" "}
                              {new Date(
                                bus.generalInfo?.pollutionControlDate,
                              ).toLocaleDateString("en-GB")}{" "}
                              To{" "}
                              {new Date(
                                bus.generalInfo?.pollutionControlExpiryDate,
                              ).toLocaleDateString("en-GB")}
                              <br />
                              <span class="text-dark">
                                GPRS Status
                              </span> &mdash; {bus.generalInfo?.gprsStatus}{" "}
                              <br />
                              <span class="text-dark">
                                CCTV Status
                              </span> &mdash; {bus.generalInfo?.cctvInstalled}
                              <br />
                              <span class="text-dark">Owner</span> &mdash;{" "}
                              {bus.generalInfo?.ownerName}
                              <hr className="w-25" />
                              <span class="text-dark">
                                Insurance Policy No
                              </span>{" "}
                              &mdash; {bus.insurance?.insurancePolicyNumber}{" "}
                              <br />
                              <span class="text-dark">
                                Insurance Amount
                              </span>{" "}
                              &mdash; {bus.insurance?.insuranceAmount} <br />
                              <span class="text-dark">Insurance Date</span>{" "}
                              &mdash;{" "}
                              {new Date(
                                bus.insurance?.insuranceDate,
                              ).toLocaleDateString("en-GB")}{" "}
                              To{" "}
                              {new Date(
                                bus.insurance?.insuranceExpiryDate,
                              ).toLocaleDateString("en-GB")}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <Link
                                  to="/update-bus"
                                  state={{
                                    vehicleNumber:
                                      bus.generalInfo?.vehicleNumber,
                                  }}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </Link>
                                <span
                                  onClick={() => handleLeftBus(bus)}
                                  class="badge bg-warning-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Left Bus
                                </span>
                                <span
                                  onClick={() =>
                                    handleDelete(bus.generalInfo?.vehicleNumber)
                                  }
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          to="/add-bus"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Bus
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Transport Management</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Bus Details
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Bus Details
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.8 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportAllBusesExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  All Bus Details
                                </span>
                                <span
                                  onClick={handleExportAllBusesCCTVStatusExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  CCTV Wise Report
                                </span>
                                <span
                                  onClick={handleExportAllBusesGPRSStatusExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  GPRS Wise Report
                                </span>
                                <span
                                  onClick={handleExportAllBusesOwnerWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Bus Details Owner Wise Report
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default BusDetails;
